import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { motion, useAnimation } from 'framer-motion';
import './aboutus.css';

function AboutUs() {
  // Animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    } else {
      controls.start('hidden');
    }
  }, [controls, inView]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="about-us-container">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-overlay"></div>
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <h1 className='cecoa-heading'>About CECOA</h1>
          <p className="cecoa-subtitle">Committed to excellence in eye care since 2009</p>
        </motion.div>
      </section>

      {/* Introduction Section */}
      <motion.section 
        className="introduction-section"
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <h2>Who We Are</h2>
          <div className="divider"></div>
        </motion.div>
        
        <div className="intro-content">
          <motion.div className="intro-text" variants={itemVariants}>
            <p>
            The Ceylon Eye Care Optometric Association (CECOA) serves as the premier professional organization for 
            optometrists in Sri Lanka, offering unparalleled membership benefits designed to elevate your practice a
            nd professional standing. Since our establishment in 2009, we have championed the advancement of optometric standards 
            while providing members with exclusive access to continuing professional development opportunities. 
            Our comprehensive networking platform connects you with peers nationwide through our practitioner directory, 
            structured mentorship programs, and regular regional meetups, fostering valuable professional relationships and 
            knowledge exchange.
            </p>
            <p>
            As the collective voice representing over 500 eye care professionals across Sri Lanka, CECOA actively safeguards 
            and promotes the interests of our members through powerful advocacy initiatives. We provide direct representation 
            in health policy discussions, offer legal support for practice-related matters, and maintain rigorous practice standards 
            to ensure the highest quality of patient care. Beyond professional advocacy, our business growth programs deliver 
            tangible financial benefits, including exclusive supplier discounts, practical marketing resources for your practice, 
            and strategic insurance partnerships that enhance both your service offerings and bottom line.
            </p>
            <p>
            CECOA stands at the forefront of Sri Lanka's vision health landscape, driving initiatives that improve nationwide access 
            to quality eye care while supporting our members' professional journey at every stage. Our multifaceted approach combines 
            professional development, community building, and strong advocacy to create an ecosystem where optometrists can thrive 
            clinically, professionally, and financially. By uniting practitioners under a shared mission, we amplify our impact on 
            public eye health while empowering each member with the resources, connections, and representation needed to excel in 
            this vital healthcare profession.
            </p>
          </motion.div>
          
          <motion.div className="intro-image" variants={itemVariants}>
            <img src="/logo192.jpg" alt="CECOA team" />
          </motion.div>
        </div>
      </motion.section>

      {/* Mission and Vision */}
      <section className="mission-vision-section">
        <div className="mv-container">
          <motion.div 
            className="mv-card mission-card"
            whileHover={{ scale: 1.03 }}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="mv-icon">
              <img src="/round.png" alt="Mission icon" />
            </div>
            <h3>Our Mission</h3>
            <p>
              To enhance the quality and accessibility of eye care services in Sri Lanka through 
              professional development, public education, and advocacy, ensuring optimal vision health 
              for all citizens.
            </p>
          </motion.div>
          
          <motion.div 
            className="mv-card vision-card"
            whileHover={{ scale: 1.03 }}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="mv-icon">
              <img src="/round.png" alt="Vision icon" />
            </div>
            <h3>Our Vision</h3>
            <p>
              A Sri Lanka where every individual has access to comprehensive eye care services and 
              where preventable vision impairment is eliminated through education, early detection, 
              and quality treatment.
            </p>
          </motion.div>
        </div>
      </section>

 
{/* History Section  */}
    <section className="history-section">
      <motion.div 
        className="section-header"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2>Our History</h2>
        <div className="divider"></div>
      </motion.div>
      
      <div className="timeline">
        {timelineData.map((item, index) => (
          <motion.div 
            key={index}
            className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <div className="timeline-content">
              <h4>{item.year}</h4>
              {item.event.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            <div className="timeline-marker"></div>
          </motion.div>
        ))}
      </div>
    </section>











      {/* Membership CTA */}
      <motion.section 
        className="membership-cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="cta-content">
          <h3>Join Our Professional Community</h3>
          <p>
            Become part of Sri Lanka's leading optometric association and access exclusive benefits, 
            continuing education, and networking opportunities.
          </p>
          <Link to="/joinusform">
          <button className="cta-button">Join Now </button></Link>
        </div>
      </motion.section>
    </div>
  );
}







// Sample data (would typically come from API or separate file)
const timelineData = [
  { year: "2009", event: "Established CEOA founded by Optom. Naresh Pradhan" },
  { 
    year: "2009", 
    event: "First World Sight Day walk in Sri Lanka\nStarted the celebrate Christmas party\nStarted Annual Cricket Fiessta"
  },
  { 
    year: "2010", 
    event: "Developed Code of Ethics\nPartnership with Ceylon Council of Optometry and Orthoptist\nStarted to Celebrate Sri Lanka New Year"
  },
  { year: "2012", event: "Launched Official Logo" },
  { year: "2024", event: "Membership with World council of Optometry and Asia Pacific Counsil of Optometry" }
];



export default AboutUs;